import { useState } from 'react'
import axios from 'axios'
import { useQuery } from '@tanstack/react-query'
import { extractErrorMessage } from '../utils/apitools'
import useAccount from './useAccount'
import useSnackbar from './useSnackbar'
import useLoading from './useLoading'
import { API_BASE_URL } from '../constants/system'

import useChat from './useChat'
import {
  ThemeAIImportConfig,
  SourceVariable,
  CarbonInterviewRawDataRow,
  AutoCoderPipelineRecord,
  AutoCoderRecordData,
  AutoCoderFramework,
  AutoCoderCode,
} from '../types/gotypes'
import type {
  ChatCompletion,
  ChatCompletionMessageParam,
} from 'openai/resources/chat/completions'
import { GenNode } from '../types/carbon/models/GenNode'

interface ThemeAITestConfig {
  name: string
  description: string
  customInstructions: string
  batch_size: number
}

// Response format from LLM function call - themes now include descriptions
interface ExtractedTheme {
  name: string
  description: string
}

interface ExtractThemesResponse {
  themes: ExtractedTheme[]
}

interface MatchThemesResponse {
  records: AutoCoderPipelineRecord[]
  frameworks: AutoCoderFramework[]
}

export const useThemeAI = ({
  customer,
  job,
}: {
  customer: string
  job: string
}) => {
  const account = useAccount()
  const snackbar = useSnackbar()
  const loading = useLoading()

  const [variable, setVariable] = useState<SourceVariable>()
  const [variableGenNode, setVariableGenNode] = useState<GenNode>()
  const [data, setData] = useState<CarbonInterviewRawDataRow[]>([])
  const [themes, setThemes] = useState<AutoCoderCode[]>([])

  // this is used to merge the preview autocoder records with the interview data
  const [autocoderRecordMap, setAutocoderRecordMap] = useState<Record<string, AutoCoderRecordData>>({})
  const [testRecords, setTestRecords] = useState<AutoCoderPipelineRecord[]>([])
  const [testResultsModalOpen, setTestResultsModalOpen] = useState(false)

  const [frameworks, setFrameworks] = useState<AutoCoderFramework[]>([])
  const [extractIsLoading, setExtractIsLoading] = useState(false)
  const [extractError, setExtractError] = useState<string>('')
  const [matchError, setMatchError] = useState<string>('')
  const [testProgress, setTestProgress] = useState({ current: 0, total: 0 })

  const extractChat = useChat({
    customer,
    job,
    url: `${API_BASE_URL}/themeai/${customer}/${job}/themes/chat`,
  })

  const extractThemesFromChatMessage = (message: ChatCompletionMessageParam): AutoCoderCode[] | undefined => {
    if (message.role !== 'assistant') return
    if (!('tool_calls' in message) || !message.tool_calls) return

    for (const toolCall of message.tool_calls) {
      if (toolCall.type !== 'function') continue
      if (toolCall.function.name !== 'theme_list') continue

      try {
        const args = JSON.parse(toolCall.function.arguments) as ExtractThemesResponse
        // Convert ExtractedTheme[] to AutoCoderCode[]
        return (args.themes || []).map((theme): AutoCoderCode => ({
          code: theme.name, // Use name as code
          text: theme.name, // Use name as display text
          description: theme.description,
        }))
      } catch (error) {
        const errorMsg = `Failed to parse function call arguments: ${error}`
        console.error(errorMsg)
        snackbar.error(errorMsg)
        return
      }
    }
  }

  // this is the auto button where we ask them for a number of themes and the batch size
  // and then loop over in those batches to build up the theme list
  // this is an alternative to the chat based route and it means we can evaluate which is
  // better for theme extraction - the key is that we don't have a way of iterating over batch size
  // in the chat based route - it's a "include all the data in the single request" route
  const extractThemes = async (config: { themeCount: number; batchSize: number; customInstructions: string }) => {
    if (!customer || !job || !account.user) return
    if (data.length === 0) {
      snackbar.error('No data available - please wait for data to load')
      return
    }
    setExtractIsLoading(true)
    setExtractError('')
    loading.setLoading(true)
    loading.setProgress('Extracting Themes', data.length, 0)

    // Use variable or a default for non-Carbon data sources
    const effectiveVariable = variable || {
      name: 'source_text',
      folder_path: '',
      question: 'Source Text',
    }

    const prompt = `
Please extract exactly ${config.themeCount} themes from the data and merge them with the previous themes.

${config.customInstructions}
`
    try {
      let currentThemes = themes
      for (let i = 0; i < data.length; i += config.batchSize) {
        const batch = data.slice(i, i + config.batchSize)

        const res = await axios.post<ChatCompletion>(
          `${API_BASE_URL}/themeai/${customer}/${job}/themes/extract`,
          {
            existing_themes: currentThemes,
            variable: effectiveVariable,
            rows: batch,
            messages: [...extractChat.messages, { role: 'user', content: prompt }],
          },
        )
        loading.setProgress(`extracting themes: batch ${i / config.batchSize + 1} of ${Math.ceil(data.length / config.batchSize)}`, data.length, i)

        const message = res.data.choices[0].message
        const extractedThemes = extractThemesFromChatMessage(message)

        if(extractedThemes === undefined) {
          throw new Error('No themes extracted')
        }

        // Function call found - update themes and add system message
        currentThemes = extractedThemes
        setThemes(currentThemes)
      }
      extractChat.setMessages([
        ...extractChat.messages,
        { role: 'user', content: prompt },
        { role: 'system', content: `Extracted ${currentThemes.length} themes from the data` }
      ])
      snackbar.success(`${currentThemes.length} Themes extracted successfully`)
    } catch (error) {
      setExtractError(extractErrorMessage(error))
    } finally {
      setExtractIsLoading(false)
      loading.setLoading(false)
      loading.setProgress('', 0, 0)
    }
  }

  const chat = async (
    model: string,
    prompt: string,
    context?: { data: CarbonInterviewRawDataRow[]; variable: SourceVariable },
  ) => {
    if (!customer || !job || !account.user) return
    const requestData = context?.data || data
    const requestVariable = context?.variable || variable
    if (requestData.length === 0) {
      snackbar.error('No data available - please wait for data to load')
      return
    }
    setExtractIsLoading(true)
    setExtractError('')

    extractChat.setMessages([...extractChat.messages, { role: 'user', content: prompt }])

    // Use variable or a default for non-Carbon data sources
    const effectiveVariable = requestVariable || {
      name: 'source_text',
      folder_path: '',
      question: 'Source Text',
    }

    try {
      const res = await axios.post<ChatCompletion>(
        `${API_BASE_URL}/themeai/${customer}/${job}/themes/extract`,
        {
          existing_themes: themes,
          variable: effectiveVariable,
          rows: requestData,
          messages: [...extractChat.messages, { role: 'user', content: prompt }],
        },
      )

      const message = res.data.choices[0].message

      const extractedThemes = extractThemesFromChatMessage(message)

      if(extractedThemes === undefined) {
        extractChat.setMessages([...extractChat.messages, { role: 'user', content: prompt }, message])
      } else {
        extractChat.setMessages([
          ...extractChat.messages,
          { role: 'user', content: prompt },
          { role: 'system', content: `Extracted ${extractedThemes.length} themes from the data` }
        ])
        setThemes(extractedThemes)
        snackbar.success(`${extractedThemes.length} Themes extracted successfully`)
      }

      extractChat.setInput('')
    } catch (error) {
      setExtractError(extractErrorMessage(error))
    } finally {
      setExtractIsLoading(false)
    }
  }

  /**
   * Extract unique suggestion codes from autocoder records as AutoCoderCode[]
   */
  const extractSuggestionCodes = (records: AutoCoderPipelineRecord[]): AutoCoderCode[] => {
    const codeMap = new Map<string, AutoCoderCode>()
    records.forEach(record => {
      record.data?.suggestions?.forEach(suggestion => {
        const code = suggestion.match?.code
        if (code && !codeMap.has(code)) {
          codeMap.set(code, {
            code: code,
            text: code,
            description: suggestion.match?.explanation || '',
          })
        }
      })
    })
    return Array.from(codeMap.values())
  }

  const testImport = async (config: ThemeAITestConfig) => {
    if (!customer || !job || !account.user || !variable) return
    if (data.length === 0) return

    // Reset and open test results modal
    setTestRecords([])
    setFrameworks([])
    setMatchError('')
    setTestProgress({ current: 0, total: data.length })
    setTestResultsModalOpen(true)

    try {
      const testConfig: ThemeAIImportConfig = {
        ...config,
        themes,
        suggestions: [],
        variable,
        messages: extractChat.messages.map(msg => ({
          role: msg.role,
          content: typeof msg.content === 'string' ? msg.content : '',
        })),
        prompt: config.customInstructions,
      }

      // Accumulate suggestions across batches
      let accumulatedSuggestions: AutoCoderCode[] = []

      for (let i = 0; i < data.length; i += config.batch_size) {
        const batch = data.slice(i, i + config.batch_size)
        const res = await axios.post<MatchThemesResponse>(
          `${API_BASE_URL}/themeai/${customer}/${job}/themes/match/test`,
          {
            config: testConfig,
            rows: batch,
          },
        )
        loading.setProgress('Matching Themes', data.length, i)

        // Prepend new batch results to the beginning (newest first)
        setTestRecords(prev => [...res.data.records, ...prev])
        setFrameworks(res.data.frameworks)

        // Extract suggestions from this batch and accumulate them (dedupe by code)
        const batchSuggestions = extractSuggestionCodes(res.data.records)
        const existingCodes = new Set(accumulatedSuggestions.map(s => s.code))
        const newSuggestions = batchSuggestions.filter(s => !existingCodes.has(s.code))
        accumulatedSuggestions = [...accumulatedSuggestions, ...newSuggestions]

        // Update testConfig with accumulated suggestions for next batch
        testConfig.suggestions = accumulatedSuggestions

        // Update progress
        setTestProgress({ current: i + batch.length, total: data.length })
      }
      snackbar.success(`${data.length} records processed`)
    } catch (error) {
      const errorMsg = extractErrorMessage(error)
      setMatchError(errorMsg)
      snackbar.error(errorMsg)
    } finally {
      setExtractIsLoading(false)
    }
  }

  const updateThemes = (themes: AutoCoderCode[]) => {
    setThemes(themes)
    setAutocoderRecordMap({})
    setFrameworks([])
  }

  const closeTestResults = () => {
    setTestResultsModalOpen(false)
    setTestRecords([])
    setFrameworks([])
    setMatchError('')
  }

  const examplePromptsQuery = useQuery({
    queryKey: ['themeai_example_prompts', { customer, job }],
    enabled: Boolean(customer && job && account.user),
    queryFn: async () => {
      if (!customer || !job || !account.user) return []
      try {
        const res = await axios.get<string[]>(`${API_BASE_URL}/themeai/${customer}/${job}/themes/example-extract-prompts`)
        return res.data
      } catch (error) {
        throw new Error(extractErrorMessage(error))
      }
    },
  })

  return {
    extractThemes,
    chat,
    testImport,
    updateThemes,
    variable, setVariable,
    variableGenNode, setVariableGenNode,
    data, setData,
    themes, setThemes,

    extractChat,
    extractError,
    extractIsLoading,
    examplePrompts: examplePromptsQuery.data || [],

    testRecords,
    frameworks,
    testResultsModalOpen,
    closeTestResults,
    matchError,
    testProgress,
  }
}

export type ThemeAIController = ReturnType<typeof useThemeAI>

export default useThemeAI
